<template>
  <div class="uav-viewer" :class="{ 'mobile-mode': isMobile, 'embedded-mode': !fullscreen }">
    <!-- 顶部标题栏 -->
    <div class="header-bar" :class="{ 'float-mode': floatHeader }">
      <div class="header-content">
        <div class="header-title">{{ projectName }} <span v-if="showSubtitle && currentView" >| {{ currentView.name }}</span></div>
        <div v-if="projectDescription" class="header-subtitle">{{ projectDescription }}</div>
        
      </div>
    </div>

    <!-- 控制按钮 - 右下角 -->
    <div class="controls controls-bottom-right">
      <button 
        v-show="showUI"
        class="control-btn"
        :class="{ active: autoRotate }"
        @click="toggleAutoRotate"
        :title="isMobile ? '' : '自动旋转'"
      >
        <BaseIcon v-if="useIcons" icon="play" :size="iconSize" />
        <span v-else class="icon">🔄</span>
      </button>
      <button 
        v-if="showPhotoBtn"
        v-show="showUI"
        class="control-btn"
        @click="takePhoto"
        :title="isMobile ? '' : '拍照'"
      >
        <BaseIcon icon="photo" :size="iconSize" />
      </button>
      <button 
        v-if="enableAnnotation && isUserLoggedIn"
        v-show="showUI"
        class="control-btn"
        :class="{ active: annotationMode }"
        @click="toggleAnnotationMode"
        :title="isMobile ? '' : '批注模式'"
      >
        <BaseIcon v-if="useIcons" icon="md-chatboxes" :size="iconSize" />
        <span v-else class="icon">{{ annotationMode ? '✓' : '📝' }}</span>
      </button>
      <button 
        v-if="currentSceneSandbox && currentSceneViews.length > 0"
        v-show="showUI"
        class="control-btn"
        :class="{ active: showSandboxInternal }"
        @click="toggleSandbox"
        :title="isMobile ? '' : '显示沙盒'"
      >
        <BaseIcon icon="location" :size="iconSize" />
      </button>
      <button 
        v-if="enableAnnotation"
        v-show="showUI"
        class="control-btn"
        :class="{ active: showAnnotationsInternal && annotationCount > 0 }"
        @click="toggleAnnotations"
        :title="isMobile ? '' : '显示批注'"
      >
          <div style="display:flex;flex-direction:column;align-items:center;">
            <BaseIcon icon="bbs" :size="iconSize - 2" style="margin-top:5px;" />
            <div class="icon-text" style="font-size:12px;">{{ annotationCount }}</div>
          </div>
       
      </button>
      <button 
        class="control-btn"
        @click="toggleUI"
        :title="isMobile ? '' : '隐藏/显示UI'"
      >
        <BaseIcon icon="richangbaoji01" :size="iconSize" />
      </button>
    </div>

    <!-- 右上角沙盒 -->
    <div v-if="showUI && showSandboxInternal && currentSceneSandbox && currentSceneViews.length > 0" class="sandbox-container">
      <SandboxMap
        :sandbox="currentSceneSandbox"
        :views="currentSceneViews"
        :selected-view="currentView"
        :current-rotation="currentRotation"
        @select-view="handleSandboxSelectView"
      />
    </div>

    <!-- 批注模式提示 -->
    <div v-if="annotationMode" class="annotation-tip">
      💡 批注模式已开启，点击全景图任意位置添加批注
    </div>

    <!-- 场景渲染区域 -->
    <div class="render-container">
      <UAVPanorama
        v-if="currentView"
        ref="panorama"
        :src="currentView.image_url"
        :fast-url="currentView.fast_url"
        :project-id="projectId"
        :project-creator-id="projectCreatorId"
        :view-id="currentView.id"
        :scene-id="currentScene ? currentScene.id : null"
        :option="currentViewOption"
        :enable-annotation-mode="annotationMode"
        :show-annotations="showAnnotationsInternal"
        @add-annotation-click="handleAddAnnotationClick"
        @load="handlePanoramaLoad"
        @annotation-interaction="handleAnnotationInteraction"
        @rotation-change="handleRotationChange"
       
      />
      <div v-else class="empty-state">
        <div class="empty-icon">🎬</div>
        <div class="empty-text">请选择一个场景和视图</div>
      </div>
    </div>

    <!-- 场景列表 - 简化版 -->
    <div v-if="showUI && scenes.length > 1" class="scene-list">
      <div class="scene-list-body">
        <div 
          v-for="scene in scenes" 
          :key="scene.id"
          class="scene-item"
          :class="{ active: currentScene && currentScene.id === scene.id }"
          @click="handleSceneClick(scene, $event)"
          @touchend="handleSceneClick(scene, $event)"
        >
          <div class="scene-name">{{ scene.name }}</div>
        </div>
      </div>
    </div>

    <!-- 底部视图列表 -->
    <div v-show="showUI" class="view-list-container">
      <div 
        class="view-list" 
        ref="viewList"
        @mousedown="handleViewListMouseDown"
        @mouseleave="handleViewListMouseUp"
        @touchstart="handleViewListTouchStart"
        @touchmove="handleViewListTouchMove"
        @touchend="handleViewListTouchEnd"
        @wheel.prevent="handleViewListWheel"
      >
        <div 
          v-for="view in currentSceneViews" 
          :key="view.id"
          class="view-item"
          :class="{ active: currentView && currentView.id === view.id }"
          :data-view-id="view.id"
          @click="handleViewClick(view, $event)"
          @touchend="handleViewClick(view, $event)"
        >
          <div class="view-image">
            <img 
              :src="getThumbUrl(view)" 
              :alt="view.name"
              @error="handleImageError"
            />
            <!-- 视角旋转指示器 -->
            <div 
              v-if="currentView && currentView.id === view.id && currentRotation"
              class="view-rotation-indicator"
              :style="{ '--rotation': `${currentRotation.x}deg` }"
            ></div>
            <div class="view-mask"></div>
            <div class="view-name">{{ view.name }}</div>
          </div>
        </div>
        <div v-if="currentSceneViews.length === 0" class="empty-hint">
          请选择场景
        </div>
      </div>
    </div>

    <!-- 添加批注弹窗 -->
    <div v-if="showAnnotationDialog" class="annotation-dialog-overlay" @click="closeAnnotationDialog">
      <div class="annotation-dialog" @click.stop>
        <div class="annotation-dialog-header">
          <h3>添加批注</h3>
          <button class="close-btn" @click="closeAnnotationDialog">×</button>
        </div>
        <div class="annotation-dialog-body">
          <textarea 
            v-model="newAnnotationText" 
            placeholder="请输入批注内容..."
            class="annotation-textarea"
            rows="4"
            ref="annotationTextarea"
          ></textarea>
        </div>
        <div class="annotation-dialog-footer">
          <button class="btn-cancel" @click="closeAnnotationDialog">取消</button>
          <button class="btn-save" @click="saveAnnotation">保存</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import UAVPanorama from '@/components/uav/render.vue';
import SandboxMap from '@/components/uav/sandbox.vue';
import BaseIcon from '@/components/base/Icon.vue';

export default {
  name: 'UAVViewer',
  components: {
    UAVPanorama,
    SandboxMap,
    BaseIcon,
  },
  props: {
    // 项目ID
    projectId: {
      type: [String, Number],
      required: true,
    },
    // 是否使用公共API
    usePublicApi: {
      type: Boolean,
      default: false,
    },
    // 控制按钮位置: 'top-center' | 'side-left'
    controlsPosition: {
      type: String,
      default: 'top-center',
      validator: (value) => ['top-center', 'side-left'].includes(value),
    },
    // 是否显示子标题（当前视图名）
    showSubtitle: {
      type: Boolean,
      default: false,
    },
    // 是否显示拍照按钮
    showPhotoBtn: {
      type: Boolean,
      default: false,
    },
    // 是否启用批注功能
    enableAnnotation: {
      type: Boolean,
      default: true,
    },
    // 是否显示沙盒
    showSandbox: {
      type: Boolean,
      default: true,
    },
    // 是否使用图标组件
    useIcons: {
      type: Boolean,
      default: false,
    },
    // 是否浮动头部
    floatHeader: {
      type: Boolean,
      default: false,
    },
    // 是否全屏展示（false 时在框架容器内展示）
    fullscreen: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      projectName: '',
      projectDescription:'',
      projectCreatorId: null,
      scenes: [],
      currentScene: null,
      currentView: null,
      autoRotate: false,
      annotationMode: false,
      canScrollLeft: false,
      canScrollRight: false,
      showAnnotationDialog: false,
      newAnnotationText: '',
      pendingAnnotationClick: null,
      cachedViewOption: null,
      lastViewId: null,
      lastAutoRotate: null,
      lastInteractionTime: 0,
      interactionCooldown: 300,
      isLoadingData: false,
      dataLoaded: false,
      isMobile: false,
      autoRotateTimer: null,
      showSandboxInternal: true, // 内部控制沙盒显示状态
      showAnnotationsInternal: true, // 内部控制批注显示状态
      // 拖拽滚动相关
      isDraggingViewList: false,
      viewListDragStartX: 0,
      viewListScrollStart: 0,
      viewListDragStartY: 0,
      viewListDragMoved: false,
      // 触摸事件相关
      touchStartX: 0,
      touchStartY: 0,
      touchMoved: false,
      // 当前视角旋转角度
      currentRotation: null,
      // UI组件显示/隐藏
      showUI: true,
    };
  },
  computed: {
    annotationCount() {
      return this.currentScene?.annotations?.length || 0;
    },
    isUserLoggedIn() {
      try {
        const session = this.$store.getters['session/session'];
        return !!(session && session.id);
      } catch (error) {
        return false;
      }
    },
    currentSceneViews() {
      if (!this.currentScene || !this.currentScene.views) {
        return [];
      }
      // 按order排序，order小的在前，如果没有order则按id排序
      return [...this.currentScene.views].sort((a, b) => {
        const orderA = a.order !== undefined && a.order !== null ? a.order : 999999;
        const orderB = b.order !== undefined && b.order !== null ? b.order : 999999;
        if (orderA !== orderB) {
          return orderA - orderB;
        }
        // 如果order相同，按id排序
        return (a.id || 0) - (b.id || 0);
      });
    },
    // 当前场景的沙盒
    currentSceneSandbox() {
      return this.currentScene?.sandbox || null;
    },
    currentViewOption() {
      if (!this.currentView) {
        return Object.freeze({
          x: 0,
          y: 0,
          zoom: 75,
          enableAnnotation: this.enableAnnotation,
          autoRotate: false,
        });
      }
      
      // 使用缓存机制，避免频繁创建新对象导致组件重新渲染
      const viewId = this.currentView.id;
      const viewOption = this.currentView.option || {};
      if (
        this.cachedViewOption &&
        this.lastViewId === viewId &&
        this.lastAutoRotate === this.autoRotate &&
        this.cachedViewOption.x === (viewOption.x || 0) &&
        this.cachedViewOption.y === (viewOption.y || 0) &&
        this.cachedViewOption.zoom === (viewOption.zoom || 75)
      ) {
        return this.cachedViewOption;
      }
      
      // 创建新对象并缓存
      const option = Object.freeze({
        x: viewOption.x || 0,
        y: viewOption.y || 0,
        zoom: viewOption.zoom || 75,
        enableAnnotation: this.enableAnnotation,
        autoRotate: this.autoRotate,
      });
      
      this.cachedViewOption = option;
      this.lastViewId = viewId;
      this.lastAutoRotate = this.autoRotate;
      
      return option;
    },
    iconSize() {
      return this.isMobile ? 18 : 20;
    },
  },
  watch: {
    currentSceneViews() {
      this.$nextTick(() => {
        this.updateScrollButtons();
      });
    },
    projectId: {
      handler(newId, oldId) {
        // 当 projectId 变化时，重新加载数据
        if (newId && newId !== oldId) {
          this.dataLoaded = false;
          this.scenes = [];
          this.currentScene = null;
          this.currentView = null;
          this.loadProjectData();
        }
      },
      immediate: false,
    },
    usePublicApi: {
      handler(newVal) {
        // 当 usePublicApi 变化时，重新加载数据
        if (this.projectId) {
          this.dataLoaded = false;
          this.scenes = [];
          this.currentScene = null;
          this.currentView = null;
          this.loadProjectData();
        }
      },
      immediate: false,
    },
  },
  mounted() {
    this.checkMobile();
    
    // 如果使用公共API，直接加载数据，不需要等待登录
    if (this.usePublicApi) {
      if (this.projectId) {
        this.loadProjectData();
      }
    } else {
      // 如果需要登录，等待登录后再加载
      this.$api.afterLogin(() => {
        if (this.projectId) {
          this.loadProjectData();
        }
      });
    }
    
    // 默认开启自动旋转
    this.autoRotate = true;
    
    // 初始化沙盒显示状态（使用 prop 的默认值）
    this.showSandboxInternal = this.showSandbox;
    
    // 监听视图列表滚动
    this.$nextTick(() => {
      const viewList = this.$refs.viewList;
      if (viewList) {
        viewList.addEventListener('scroll', this.updateScrollButtons);
      }
    });

    // 监听窗口调整大小
    window.addEventListener('resize', this.handleResize);
    window.addEventListener('orientationchange', this.handleOrientationChange);
    
    // 全局监听鼠标移动和抬起事件，用于拖拽
    document.addEventListener('mousemove', this.handleViewListMouseMove);
    document.addEventListener('mouseup', this.handleViewListMouseUp);
  },
  beforeDestroy() {
    // 清理视图列表滚动监听
    const viewList = this.$refs.viewList;
    if (viewList) {
      viewList.removeEventListener('scroll', this.updateScrollButtons);
    }
    
    // 清理窗口事件监听
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('orientationchange', this.handleOrientationChange);
    
    // 清理全局鼠标事件监听
    document.removeEventListener('mousemove', this.handleViewListMouseMove);
    document.removeEventListener('mouseup', this.handleViewListMouseUp);
    
    // 清理拖拽滚动相关的事件监听（如果已添加）
    if (viewList) {
      viewList.removeEventListener('mousedown', this.handleViewListMouseDown);
      viewList.removeEventListener('mouseleave', this.handleViewListMouseUp);
      viewList.removeEventListener('touchstart', this.handleViewListTouchStart);
      viewList.removeEventListener('touchmove', this.handleViewListTouchMove);
      viewList.removeEventListener('touchend', this.handleViewListTouchEnd);
    }
    
    // 清理定时器
    if (this.autoRotateTimer) {
      clearTimeout(this.autoRotateTimer);
      this.autoRotateTimer = null;
    }
    
    // 清理panorama组件
    if (this.$refs.panorama) {
      // panorama组件有自己的beforeDestroy清理逻辑
      this.$refs.panorama = null;
    }
    
    // 清理数据引用，防止内存泄漏
    this.scenes = [];
    this.currentScene = null;
    this.currentView = null;
    this.cachedViewOption = null;
  },
  methods: {
    checkMobile() {
      this.isMobile = window.innerWidth <= 768;
    },
    
    handleResize() {
      this.checkMobile();
      this.updateScrollButtons();
    },
    
    handleOrientationChange() {
      // 防止横竖屏切换时的异常缩放
      const viewport = document.querySelector('meta[name=viewport]');
      if (viewport) {
        viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no');
      }
      
      // 延迟更新以确保屏幕旋转完成
      setTimeout(() => {
        this.checkMobile();
        this.updateScrollButtons();
        // 强制重新计算布局
        if (this.$refs.panorama) {
          this.$refs.panorama.$forceUpdate();
          // 通知 panorama 组件更新相机视角
          if (this.$refs.panorama.onWindowResize) {
            this.$refs.panorama.onWindowResize();
          }
        }
        // 再次更新滚动按钮，确保布局计算完成
        this.$nextTick(() => {
          this.updateScrollButtons();
        });
      }, 300);
    },
    
    takePhoto() {
      if (this.$refs.panorama) {
        this.$refs.panorama.takePhoto();
      }
    },
    
    async loadProjectData() {
      // 检查 projectId 是否存在
      if (!this.projectId) {
        console.warn('UAVViewer: projectId 未提供，无法加载数据');
        return;
      }
      
      // 防止重复加载
      if (this.isLoadingData || this.dataLoaded) {
        return;
      }
      
      this.isLoadingData = true;
      
      try {
        // 根据配置使用不同的API接口
        const apiPath = this.usePublicApi
          ? `public/uav-projects/${this.projectId}?full=1`
          : `uav/projects/${this.projectId}?full=1`;
        
        // 加载项目信息
        const projectRes = await this.$api.get(apiPath);
        const project = projectRes.data.data;
        
        this.projectName = project.name || '无标题项目';
        this.projectDescription = project.description || '';
        this.projectCreatorId = project.created_by || null;
        
        // 处理场景和视图数据，每个场景关联自己的沙盒
        const processedScenes = project.scenes.map(scene => {
          // 查找该场景对应的沙盒（通过scene_id匹配）
          const sceneSandbox = project.sandboxes?.find(sb => sb.scene_id === scene.id) || null;
          
          // 获取该场景的视图并按order排序
          const sceneViews = project.views
            .filter(view => view.scene_id === scene.id)
            .map(view => {
              return {
                ...view,
                image_url: view.url,
                fast_url: view.fast_url,
                thumb_url: (view.avatar && view.avatar.trim()) ? view.avatar : (view.url || ''),
                // 确保 option 对象存在并包含所有必要的字段
                option: {
                  x: view.x !== undefined ? view.x : (view.option?.x || 0),
                  y: view.y !== undefined ? view.y : (view.option?.y || 0),
                  z: view.z !== undefined ? view.z : (view.option?.z || 0),
                  zoom: view.zoom !== undefined ? view.zoom : (view.option?.zoom || 75),
                },
                // 确保沙盒参数存在
                sandbox_x: view.sandbox_x !== undefined ? view.sandbox_x : 50,
                sandbox_y: view.sandbox_y !== undefined ? view.sandbox_y : 50,
                sandbox_rotate: view.sandbox_rotate !== undefined ? view.sandbox_rotate : 0,
              };
            })
            // 按order排序，order小的在前，如果没有order则按id排序
            .sort((a, b) => {
              const orderA = a.order !== undefined && a.order !== null ? a.order : 999999;
              const orderB = b.order !== undefined && b.order !== null ? b.order : 999999;
              if (orderA !== orderB) {
                return orderA - orderB;
              }
              // 如果order相同，按id排序
              return (a.id || 0) - (b.id || 0);
            });
          
          return {
            ...scene,
            sandbox: sceneSandbox, // 将沙盒关联到场景
            views: sceneViews,
          };
        })
        // 按order排序场景，order小的在前，如果没有order则按id排序
        .sort((a, b) => {
          const orderA = a.order !== undefined && a.order !== null ? a.order : 999999;
          const orderB = b.order !== undefined && b.order !== null ? b.order : 999999;
          if (orderA !== orderB) {
            return orderA - orderB;
          }
          // 如果order相同，按id排序
          return (a.id || 0) - (b.id || 0);
        });
        
        this.scenes = processedScenes;
        this.dataLoaded = true;
        
        // 使用 $nextTick 确保数据已经更新完成
        this.$nextTick(() => {
          // 默认选择第一个场景
          if (!this.currentScene && this.scenes.length > 0 && this.scenes[0].views && this.scenes[0].views.length > 0) {
            this.selectScene(this.scenes[0]);
          }
        });
      } catch (error) {
        console.error('加载项目数据失败:', error);
        this.$message?.error('加载项目数据失败');
      } finally {
        this.isLoadingData = false;
      }
    },
    
    selectScene(scene, isUserAction = false) {
      // 如果已经是当前场景，不重复设置
      if (this.currentScene && this.currentScene.id === scene.id) {
        return;
      }
      
      this.currentScene = scene;
      // 清除缓存，强制重新计算
      this.cachedViewOption = null;
      this.lastViewId = null;
      this.lastAutoRotate = null;
      
      // 默认选择第一个视图
      if (scene.views && scene.views.length > 0) {
        this.selectView(scene.views[0], isUserAction);
      } else {
        this.currentView = null;
        // 场景切换后，如果不是用户操作，启动自动旋转
        if (!isUserAction) {
          this.startAutoRotate();
        }
      }
    },
    
    selectView(view, isUserAction = false) {
      // 如果已经是当前视图，不重复设置
      if (this.currentView && this.currentView.id === view.id) {
        return;
      }
      
      this.currentView = view;
      // 保存全景图的初始旋转角度，用于后续计算相对旋转
      const viewOption = view.option || {};
      view.origin_panorama_rotation = viewOption.x !== undefined ? viewOption.x : 0;
      // 保存 sandbox 的初始旋转角度，用于后续计算相对旋转
      view.origin_sandbox_rotate = view.sandbox_rotate !== undefined ? view.sandbox_rotate : 0;
      
      // 清除缓存，强制重新计算
      this.cachedViewOption = null;
      this.lastViewId = null;
      this.lastAutoRotate = null;
      
      // 如果是用户操作，暂停自动旋转；否则启动自动旋转
      if (isUserAction) {
        this.pauseAutoRotate();
      } else {
        this.startAutoRotate();
      }
    },
    
    // 启动自动旋转
    startAutoRotate() {
      // 清除之前的定时器
      if (this.autoRotateTimer) {
        clearTimeout(this.autoRotateTimer);
        this.autoRotateTimer = null;
      }
      
      this.autoRotate = true;
      // 延迟设置，确保 panorama 组件已准备好
      this.$nextTick(() => {
        if (this.$refs.panorama) {
          this.$refs.panorama.setAutoRotate(true);
        }
      });
    },
    
    // 处理全景图加载完成事件
    handlePanoramaLoad() {
      // 全景图加载完成后，如果 autoRotate 为 true，启动自动旋转
      if (this.autoRotate && this.$refs.panorama) {
        this.$refs.panorama.setAutoRotate(true);
      }
    },
    
    handleRotationChange(rotation) {
      // 更新当前视角旋转角度，用于同步略缩图指示器和 sandbox 视角
      this.currentRotation = this.currentView.origin_panorama_rotation + rotation.x;
      
      // 如果当前 view 存在，实时更新其 sandbox_rotate（用于 sandbox 视角同步）
      if (this.currentView && rotation && rotation.x !== undefined) {
        // 获取全景图的初始旋转角度（在 selectView 时保存的值）
        const initialPanoramaRotation = this.currentView.origin_panorama_rotation !== undefined 
          ? this.currentView.origin_panorama_rotation 
          : (this.currentView.option?.x || 0);
        // 获取 sandbox 的初始旋转角度（在 selectView 时保存的值）
        const initialSandboxRotation = this.currentView.origin_sandbox_rotate !== undefined 
          ? this.currentView.origin_sandbox_rotate 
          : (this.currentView.sandbox_rotate || 0);
        
        // 计算旋转增量（相对于初始角度的变化）
        const deltaRotation = rotation.x - initialPanoramaRotation;
        // 将增量加到 sandbox 的初始角度上
        const newSandboxRotation = initialSandboxRotation + deltaRotation;
        
        // 更新当前 view 的 sandbox_rotate，确保 sandbox 中的视角指示器实时同步
        this.$set(this.currentView, 'sandbox_rotate', newSandboxRotation);
      }
    },
    
    // 处理批注交互事件（点击或悬停）
    handleAnnotationInteraction() {
      this.pauseAutoRotate();
    },
    
    handleSceneClick(scene, event) {
      // 防止移动端touchend和click重复触发
      const now = Date.now();
      if (now - this.lastInteractionTime < this.interactionCooldown) {
        if (event) {
          event.preventDefault();
          event.stopPropagation();
        }
        return;
      }
      this.lastInteractionTime = now;
      
      // 用户点击场景，传递isUserAction=true
      this.selectScene(scene, true);
    },
    
    handleViewClick(view, event) {
      // 如果是触摸事件且发生了滑动，不触发点击
      if (event && event.type === 'touchend' && this.touchMoved) {
        this.touchMoved = false;
        return;
      }
      
      // 如果是鼠标事件且发生了拖拽，不触发点击
      if (event && event.type === 'click' && this.viewListDragMoved) {
        this.viewListDragMoved = false;
        return;
      }
      
      // 防止移动端touchend和click重复触发
      const now = Date.now();
      if (now - this.lastInteractionTime < this.interactionCooldown) {
        if (event) {
          event.preventDefault();
          event.stopPropagation();
        }
        return;
      }
      this.lastInteractionTime = now;
      
      // 用户点击视图，传递isUserAction=true
      this.selectView(view, true);
    },
    
    handleSandboxSelectView(viewId) {
      const view = this.currentSceneViews.find(v => v.id === viewId);
      if (view) {
        // 沙盒选择视图也算用户操作
        this.selectView(view, true);
      }
    },
    
    toggleAutoRotate() {
      this.autoRotate = !this.autoRotate;
      // 清除缓存，强制重新计算
      this.cachedViewOption = null;
      this.lastAutoRotate = null;
      if (this.$refs.panorama) {
        this.$refs.panorama.setAutoRotate(this.autoRotate);
      }
    },
    
    toggleAnnotationMode() {
      if (!this.isUserLoggedIn) {
        this.$message?.warning('请先登录');
        return;
      }
      this.annotationMode = !this.annotationMode;
      
      // 如果开启批注模式，关闭自动旋转
      if (this.annotationMode && this.autoRotate) {
        this.autoRotate = false;
        if (this.$refs.panorama) {
          this.$refs.panorama.setAutoRotate(false);
        }
      }
    },
    
    toggleSandbox() {
      this.showSandboxInternal = !this.showSandboxInternal;
    },
    
    toggleAnnotations() {
      if(!this.annotationCount){
        return 
      }
      this.showAnnotationsInternal = !this.showAnnotationsInternal;
    },
    
    toggleUI() {
      this.showUI = !this.showUI;
    },
    
    handleAddAnnotationClick(clickData) {
      this.pendingAnnotationClick = clickData;
      this.showAnnotationDialog = true;
      this.$nextTick(() => {
        if (this.$refs.annotationTextarea) {
          this.$refs.annotationTextarea.focus();
        }
      });
    },
    
    closeAnnotationDialog() {
      this.showAnnotationDialog = false;
      this.newAnnotationText = '';
      this.pendingAnnotationClick = null;
    },
    
    async saveAnnotation() {
      if (!this.newAnnotationText.trim()) {
        this.$message?.warning('请输入批注内容');
        return;
      }
      
      if (!this.pendingAnnotationClick) {
        return;
      }
      
      try {
        const panorama = this.$refs.panorama;
        if (panorama) {
          await panorama.addAnnotationAtPosition(
            this.pendingAnnotationClick.clientX,
            this.pendingAnnotationClick.clientY,
            this.newAnnotationText
          );
          this.$message?.success('批注添加成功');
          this.closeAnnotationDialog();
        }
      } catch (error) {
        console.error('保存批注失败:', error);
        this.$message?.error('保存批注失败');
      }
    },
    
    scrollViews(direction) {
      const viewList = this.$refs.viewList;
      if (!viewList) return;
      
      const scrollAmount = this.isMobile ? 200 : 300;
      const currentScroll = viewList.scrollLeft;
      
      if (direction === 'left') {
        viewList.scrollTo({
          left: currentScroll - scrollAmount,
          behavior: 'smooth'
        });
      } else {
        viewList.scrollTo({
          left: currentScroll + scrollAmount,
          behavior: 'smooth'
        });
      }
      
      setTimeout(() => {
        this.updateScrollButtons();
      }, 300);
    },
    
    // 拖拽滚动相关方法
    handleViewListMouseDown(event) {
      // 记录起始位置
      this.viewListDragStartX = event.clientX;
      this.viewListDragStartY = event.clientY;
      this.viewListDragMoved = false;
      
      const viewList = this.$refs.viewList;
      if (viewList) {
        this.viewListScrollStart = viewList.scrollLeft;
      }
      
      // 检查是否点击的是view-item内的可点击内容
      const clickedImage = event.target.closest('.view-image img');
      const clickedName = event.target.closest('.view-name');
      const clickedIndicator = event.target.closest('.view-rotation-indicator');
      
      // 如果点击的是图片、文字或指示器，不立即触发拖拽，等待判断是点击还是拖拽
      if (clickedImage || clickedName || clickedIndicator) {
        // 标记为可能的选择操作，但不阻止后续的拖拽判断
        this.isDraggingViewList = false;
        return;
      }
      
      // 在列表空白区域或view-item的边框上，立即允许拖拽
      this.isDraggingViewList = true;
      if (viewList) {
        viewList.classList.add('dragging');
      }
      // 阻止默认行为，允许拖拽
      event.preventDefault();
      // 阻止事件冒泡，避免触发其他点击事件
      event.stopPropagation();
    },
    
    handleViewListMouseMove(event) {
      // 只有在按下鼠标时才处理拖拽
      if (!this.isDraggingViewList && event.buttons === 0) {
        return;
      }
      
      const deltaX = Math.abs(event.clientX - this.viewListDragStartX);
      const deltaY = Math.abs(event.clientY - this.viewListDragStartY);
      
      // 如果移动距离超过5px，认为是拖拽操作
      if (deltaX > 5 || deltaY > 5) {
        this.viewListDragMoved = true;
        
        // 如果还没有开始拖拽，现在开始
        if (!this.isDraggingViewList) {
          this.isDraggingViewList = true;
          const viewList = this.$refs.viewList;
          if (viewList) {
            viewList.classList.add('dragging');
          }
        }
      }
      
      if (!this.isDraggingViewList) return;
      const viewList = this.$refs.viewList;
      if (!viewList) return;
      
      const scrollDeltaX = event.clientX - this.viewListDragStartX;
      viewList.scrollLeft = this.viewListScrollStart - scrollDeltaX;
      event.preventDefault();
    },
    
    handleViewListMouseUp(event) {
      // 只有在拖拽状态下才处理
      if (!this.isDraggingViewList) {
        return;
      }
      
      // 重置拖拽状态
      const wasDragging = this.isDraggingViewList;
      this.isDraggingViewList = false;
      
      const viewList = this.$refs.viewList;
      if (viewList) {
        viewList.classList.remove('dragging');
      }
      
      // 如果发生了拖拽，阻止后续的点击事件
      if (wasDragging && this.viewListDragMoved) {
        // 延迟重置，确保点击事件能正确判断
        setTimeout(() => {
          this.viewListDragMoved = false;
        }, 100);
      } else {
        // 如果没有发生拖拽，立即重置
        this.viewListDragMoved = false;
      }
    },
    
    handleViewListTouchStart(event) {
      // 记录触摸起始位置，用于判断是否滑动
      if (event.touches.length === 1) {
        this.touchStartX = event.touches[0].clientX;
        this.touchStartY = event.touches[0].clientY;
        this.touchMoved = false;
      }
      
      // 如果触摸的是view-item，不触发拖拽
      if (event.target.closest('.view-item')) {
        return;
      }
      if (event.touches.length === 1) {
        this.isDraggingViewList = true;
        this.viewListDragStartX = event.touches[0].clientX;
        const viewList = this.$refs.viewList;
        if (viewList) {
          this.viewListScrollStart = viewList.scrollLeft;
        }
        // 阻止默认行为，允许拖拽
        event.preventDefault();
      }
    },
    
    handleViewListTouchMove(event) {
      // 检测是否发生了滑动
      if (event.touches.length === 1) {
        const deltaX = Math.abs(event.touches[0].clientX - this.touchStartX);
        const deltaY = Math.abs(event.touches[0].clientY - this.touchStartY);
        // 如果移动距离超过10px，认为是滑动
        if (deltaX > 10 || deltaY > 10) {
          this.touchMoved = true;
        }
      }
      
      if (!this.isDraggingViewList || event.touches.length !== 1) return;
      const viewList = this.$refs.viewList;
      if (!viewList) return;
      
      const deltaX = event.touches[0].clientX - this.viewListDragStartX;
      viewList.scrollLeft = this.viewListScrollStart - deltaX;
      event.preventDefault();
    },
    
    handleViewListTouchEnd() {
      this.isDraggingViewList = false;
      const viewList = this.$refs.viewList;
      if (viewList) {
        viewList.classList.remove('dragging');
      }
      // 延迟重置 touchMoved，确保 click 事件能正确判断
      setTimeout(() => {
        this.touchMoved = false;
      }, 100);
    },

    handleViewListWheel(event) {
      const viewList = this.$refs.viewList;
      if (!viewList) return;

      const delta = event.deltaX !== 0 ? event.deltaX : event.deltaY;
      if (delta === 0) return;

      viewList.scrollLeft += delta;
    },
    
    updateScrollButtons() {
      const viewList = this.$refs.viewList;
      if (!viewList) return;
      
      // 使用 requestAnimationFrame 确保在布局更新后计算
      this.$nextTick(() => {
        const scrollLeft = viewList.scrollLeft;
        const scrollWidth = viewList.scrollWidth;
        const clientWidth = viewList.clientWidth;
        
        this.canScrollLeft = scrollLeft > 0;
        // 使用较小的阈值（1px）来检测是否可以向右滚动
        this.canScrollRight = scrollLeft < (scrollWidth - clientWidth - 1);
      });
    },
    
    getThumbUrl(view) {
      // 优先使用 thumb_url，如果不存在或为空，使用 image_url
      if (view.thumb_url && view.thumb_url.trim()) {
        return view.thumb_url;
      }
      return view.image_url || '';
    },
    
    handleImageError(event) {
      // 如果略缩图加载失败，尝试使用原图
      const viewItem = event.target.closest('.view-item');
      if (viewItem) {
        const viewId = viewItem.getAttribute('data-view-id');
        if (viewId) {
          const view = this.currentSceneViews.find(v => v.id === parseInt(viewId));
          if (view && view.image_url && event.target.src !== view.image_url) {
            event.target.src = view.image_url;
            return;
          }
        }
      }
      event.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTgwIiBoZWlnaHQ9IjEyMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTgwIiBoZWlnaHQ9IjEyMCIgZmlsbD0iIzMzMyIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmaWxsPSIjNjY2IiBmb250LXNpemU9IjE0IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkb21pbmFudC1iYXNlbGluZT0ibWlkZGxlIj7liqDovb3lpLHotKU8L3RleHQ+PC9zdmc+';
    },
    
    // 暂停自动旋转，3秒后恢复
    pauseAutoRotate() {
      // 清除之前的定时器
      if (this.autoRotateTimer) {
        clearTimeout(this.autoRotateTimer);
        this.autoRotateTimer = null;
      }
      
      // 暂停自动旋转
      this.autoRotate = false;
      if (this.$refs.panorama) {
        this.$refs.panorama.setAutoRotate(false);
      }
      
      // 3秒后恢复自动旋转
      this.autoRotateTimer = setTimeout(() => {
        this.autoRotate = true;
        if (this.$refs.panorama) {
          this.$refs.panorama.setAutoRotate(true);
        }
        this.autoRotateTimer = null;
      }, 3000);
    },
    
  },
};
</script>

<style scoped>
.uav-viewer {
  display: flex;
  flex-direction: column;
  background: #0a0a0a;
  overflow: hidden;
}

.uav-viewer:not(.embedded-mode) {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}

.uav-viewer.embedded-mode {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 0;
}

/* 顶部标题栏 */
.header-bar {
  height: 60px;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  display: flex;
  align-items: center;
  padding: 0 30px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  z-index: 100;
}

.header-bar.float-mode {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: auto;
  min-height: 60px;
  padding: 12px 30px;
  background: transparent;
  box-shadow: none;
  pointer-events: none;
}

.header-bar.float-mode .header-content {
  pointer-events: auto;
}

.header-title {
  font-size: 20px;
  font-weight: 600;
  color: #fff;
  letter-spacing: 0.5px;
  line-height: 1.2;
}

.header-bar.float-mode .header-title {
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
}

.header-subtitle {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.85);
  margin-top: 4px;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
  line-height: 1.2;
}

/* 控制按钮 - 右下角 */
.controls.controls-bottom-right {
  position: absolute;
  bottom: 200px;
  right: 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 30;
}

.control-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  padding: 0;
  border: none;
  color: #fff;
  font-size: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.controls.controls-bottom-right .control-btn {
  background: rgba(0, 0, 0, 0.75);
  border-radius: 12px;
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
}

.control-btn :deep(.gzicon),
.control-btn :deep(.ivu-icon) {
  color: #fff;
}

.control-btn:hover {
  transform: scale(1.1);
}

.controls.controls-bottom-right .control-btn:hover {
  background: rgba(0, 0, 0, 0.85);
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.5);
}

.control-btn.active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  box-shadow: 0 0 20px rgba(102, 126, 234, 0.6);
}

.controls.controls-bottom-right .control-btn.active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  box-shadow: 0 0 20px rgba(102, 126, 234, 0.6), 0 4px 12px rgba(0, 0, 0, 0.4);
}

.control-btn .icon {
  font-size: 20px;
}

/* 批注模式提示 */
.annotation-tip {
  position: absolute;
  bottom: 200px;
  right: 20px;
  background: rgba(102, 126, 234, 0.95);
  color: white;
  padding: 12px 24px;
  border-radius: 24px;
  font-size: 14px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  z-index: 45;
  animation: fadeInUp 0.3s ease;
  backdrop-filter: blur(10px);
  max-width: 300px;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 右上角沙盒 */
.sandbox-container {
  position: absolute;
  top: 20px;
  right: 100px;
  z-index: 20;
  width: 320px;
  height: 240px;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 12px;
  padding: 5px;
  backdrop-filter: blur(10px);
  box-shadow: 2px 2px 10px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  pointer-events: auto;
}

.sandbox-container :deep(.scene-dot) {
  cursor: pointer !important;
  pointer-events: auto;
}

.sandbox-container :deep(.scene-direction::before) {
  pointer-events: none !important;
  cursor: default !important;
}

.sandbox-container :deep(.rotation-controls) {
  display: none !important;
}

/* 场景渲染区域 */
.render-container {
  flex: 1;
  position: relative;
  overflow: hidden;
}

.empty-state {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #666;
}

.empty-icon {
  font-size: 64px;
  margin-bottom: 20px;
}

.empty-text {
  font-size: 18px;
}

/* 场景列表 - 竖排 */
.scene-list {
  position: absolute;
  bottom: 170px;
  left: 20px;
  z-index: 30;
}

.scene-list-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 400px;
  overflow-y: auto;
}

.scene-list-body::-webkit-scrollbar {
  width: 4px;
}

.scene-list-body::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 2px;
}

.scene-list-body::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
}

.scene-list-body::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}

.scene-item {
  padding: 8px 16px;
  background: rgba(0, 0, 0, 0.5);
  border: 2px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  backdrop-filter: blur(10px);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.4);
  will-change: background, border-color;
  transform: translateZ(0);
  min-width: 200px;
  max-width: 200px;
}

.scene-item:hover {
  background: rgba(0, 0, 0, 0.95);
  border-color: rgba(102, 126, 234, 0.5);
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.5);
}

.scene-item.active {
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.9) 0%, rgba(118, 75, 162, 0.9) 100%);
  border-color: #667eea;
  box-shadow: 0 0 15px rgba(102, 126, 234, 0.6);
}

.scene-name {
  font-size: 13px;
  font-weight: 500;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

/* 底部视图列表 */
.view-list-container {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  width: auto;
  max-width: min(520px, calc(100% - 40px));
  display: flex;
  justify-content: center;
  z-index: 30;
}

.view-list {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 8px 10px;
  background: rgba(0, 0, 0, 0.45);
  border-radius: 10px;
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.35);
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  cursor: grab;
  user-select: none;
  width: max-content;
  max-width: 100%;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.view-list:active {
  cursor: grabbing;
}

.view-list.dragging {
  cursor: grabbing;
  user-select: none;
}

.view-list::-webkit-scrollbar {
  display: none;
}

.view-item {
  flex-shrink: 0;
  width: 72px;
  height: 72px;
  cursor: pointer;
  border-radius: 8px;
  overflow: hidden;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
  border: 2px solid rgba(255, 255, 255, 0.85);
  will-change: transform;
  transform: translateZ(0);
  position: relative;
  box-sizing: border-box;
}

.view-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  pointer-events: none;
}

.view-item:hover {
  transform: translateY(-2px) translateZ(0);
  border-color: #fff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.35);
}

.view-item.active {
  border-color: #f59e0b;
  box-shadow: 0 0 0 1px rgba(245, 158, 11, 0.35), 0 4px 12px rgba(0, 0, 0, 0.35);
}

.view-image {
  position: relative;
  width: 100%;
  height: 100%;
  pointer-events: auto;
}

.view-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
  user-select: none;
  -webkit-user-drag: none;
}

.view-rotation-indicator {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 3px;
  height: 35%;
  background: linear-gradient(to top, rgba(102, 126, 234, 0.9), rgba(102, 126, 234, 0.5));
  transform-origin: 50% 100%;
  transform: translate(-50%, -100%) rotate(var(--rotation, 0deg));
  z-index: 2;
  pointer-events: none;
  border-radius: 2px;
  box-shadow: 0 0 8px rgba(102, 126, 234, 0.6);
  transition: transform 0.1s linear;
}

.view-mask {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 55%;
  background: linear-gradient(to bottom, transparent, rgba(0, 0, 0, 0.75));
  pointer-events: none;
}

.view-name {
  position: absolute;
  bottom: 4px;
  left: 4px;
  right: 4px;
  color: #fff;
  font-size: 10px;
  font-weight: 500;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.6);
  z-index: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.2;
}

.scroll-btn {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.85);
  border: 2px solid rgba(255, 255, 255, 0.3);
  color: #fff;
  font-size: 28px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
}

.scroll-btn:hover {
  background: rgba(102, 126, 234, 0.8);
  border-color: #667eea;
  transform: scale(1.1);
}

.scroll-btn:active {
  transform: scale(0.95);
}

.empty-hint {
  color: #666;
  font-size: 14px;
  padding: 20px;
  text-align: center;
}

/* 添加批注弹窗 */
.annotation-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
}

.annotation-dialog {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.annotation-dialog-header {
  padding: 16px 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.annotation-dialog-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.close-btn {
  background: none;
  border: none;
  color: white;
  font-size: 28px;
  line-height: 1;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background 0.2s;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.annotation-dialog-body {
  padding: 20px;
}

.annotation-textarea {
  width: 100%;
  padding: 12px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  transition: border-color 0.3s;
  box-sizing: border-box;
}

.annotation-textarea:focus {
  outline: none;
  border-color: #667eea;
}

.annotation-dialog-footer {
  padding: 16px 20px;
  background: #f8f9fa;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.btn-cancel,
.btn-save {
  padding: 8px 20px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-cancel {
  background: #e0e0e0;
  color: #666;
}

.btn-cancel:hover {
  background: #d0d0d0;
}

.btn-save {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-weight: 600;
}

.btn-save:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

/* 移动端适配 */
@media screen and (max-width: 768px) {
  .header-bar {
    height: 50px;
    padding: 0 15px;
  }

  .header-bar.float-mode {
    min-height: 50px;
    padding: 10px 15px;
  }

  .header-title {
    font-size: 16px;
  }

  .header-subtitle {
    font-size: 11px;
    margin-top: 3px;
  }

  .controls.controls-bottom-right {
    bottom: 150px;
    right: 10px;
    gap: 6px;
  }

  .control-btn {
    width: 36px;
    height: 36px;
    font-size: 16px;
  }

  .control-btn .icon {
    font-size: 16px;
  }

  .annotation-tip {
    bottom: 150px;
    right: 10px;
    font-size: 12px;
    padding: 8px 16px;
    max-width: 250px;
  }

  .sandbox-container {
    top: 60px;
    right: 20px;
    width: 200px;
    height: 150px;
    padding: 3px;
    opacity: 0.8;
    pointer-events: auto;
  }
  
  /* 确保 sandbox 不遮挡底部的 view 列表 */
  .sandbox-container :deep(.sandbox-map) {
    pointer-events: auto;
  }
  
  .sandbox-container :deep(*) {
    pointer-events: auto;
  }

  .scene-list {
    bottom: 120px;
    left: 10px;
  }

  .scene-list-body {
    max-height: 300px;
  }

  .scene-item {
    padding: 6px 12px;
    min-width: 150px;
    max-width: 150px;
  }

  .scene-name {
    font-size: 12px;
  }

  .view-list-container {
    bottom: 10px;
    max-width: min(420px, calc(100% - 20px));
  }

  .view-list {
    gap: 6px;
    padding: 6px 8px;
  }

  .view-item {
    width: 64px;
    height: 64px;
    border-radius: 7px;
  }

  .view-name {
    font-size: 9px;
    bottom: 3px;
    left: 3px;
    right: 3px;
  }

  .scroll-btn {
    width: 36px;
    height: 36px;
    font-size: 20px;
  }
}

/* 横屏优化 */
@media screen and (max-width: 1000px) and (orientation: landscape) {
  .controls.controls-bottom-right {
    bottom: 25px;
    right: 25px;
  }

  .annotation-tip {
    bottom: 100px;
    right: 10px;
    font-size: 11px;
    padding: 6px 12px;
    max-width: 200px;
  }

  .sandbox-container {
    transform: scale(0.5);
    transform-origin: top right;
  }

  .scene-list {
    bottom: 100px;
    left: 70px;
  }

  .scene-list-body {
    flex-direction: row;
    max-height: none;
    overflow-x: auto;
    overflow-y: hidden;
  }

  .scene-item {
    min-width: auto;
    max-width: 200px;
    flex-shrink: 0;
  }

  .view-list-container {
    bottom: 25px;
    max-width: min(360px, calc(100% - 20px));
  }

  .view-list {
    gap: 5px;
    padding: 5px 6px;
  }

  .view-item {
    width: 56px;
    height: 56px;
    border-radius: 6px;
  }

  .view-name {
    font-size: 8px;
    bottom: 2px;
    left: 2px;
    right: 2px;
  }

  .scroll-btn {
    width: 32px;
    height: 32px;
    font-size: 18px;
  }
}
</style>

